import { useSyncExternalStore } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Which Fiókom sections hold unsaved work — read by the SIDEBAR now.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The account screen's sections each own their draft and report whether it differs from what is
 * stored; the dot that says so used to sit in the screen's own nav. Since 13 Oct 2026 that nav is the
 * app's sidebar (settings mode, `app.tsx`), which lives above the screen — so the flags are kept
 * here, a store both can reach, rather than hoisted through the route.
 *
 * ⚠️ **Cleared when the account screen unmounts**, so a dot cannot outlive the drafts it described.
 */
let state: Readonly<Record<string, boolean>> = {};
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function setAccountDirty(section: string, dirty: boolean): void {
  if ((state[section] === true) === dirty) return;
  state = { ...state, [section]: dirty };
  emit();
}

export function clearAccountDirty(): void {
  if (Object.keys(state).length === 0) return;
  state = {};
  emit();
}

export function useAccountDirty(): Readonly<Record<string, boolean>> {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => state,
  );
}
